/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3FuelMixAutofill.ts.
 */

import type {
  FormDataResponse,
  FormField,
} from "../../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../../services/calorificValuesPublicService";
import {
  findCalorificValueForMix,
  findFuelDensityForMix,
  fuelDensityValueAndUnitFromCatalogRow,
  getFuelMixDropdownLabelForField,
  pickCalorificUnitFromOptions,
  type UnitSelectOption,
} from "../../shared/fuelMixReferenceAutofill";

/** Type definition — A3Fuel Field Name Map. */
export type A3FuelFieldNameMap = {
  mix?: string;
  quantity?: string;
  quantityUnit?: string;
  density?: string;
  densityUnit?: string;
  calorific?: string;
  calorificUnit?: string;
};

const FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";

/**
 * CMS may name the mix field `*FuelMix*` / `*fuel_mix*` or only set a label like "Fuel mix".
 * Must stay aligned with `isA3FuelMixFieldKey` so load-time autofill and on-change patch use the same field.
 */
export function findA3FuelMixField(
  formData: FormDataResponse | null
): FormField | undefined {
  const sec = formData?.formSubSections?.find((s) => s.code === FUEL_CODE);
  if (!sec?.fields) return undefined;
  const L = (s: string | undefined) => (s ?? "").toLowerCase();
  return sec.fields.find((f) => {
    const n = L(f.name);
    if (
      n.includes("fuelconsumptiondensity") ||
      n.includes("processfuelconsumptiondensity") ||
      (n.includes("fueldensity") && !n.includes("mix")) ||
      n.includes("calorific")
    ) {
      return false;
    }
    return (
      n.includes("fuelmix") ||
      n.includes("fuel_mix") ||
      (L(f.label).includes("fuel") && L(f.label).includes("mix"))
    );
  });
}

/** Exported function — discover A3Fuel Field Names. */
export function discoverA3FuelFieldNames(
  formData: FormDataResponse | null
): A3FuelFieldNameMap {
  const sec = formData?.formSubSections?.find((s) => s.code === FUEL_CODE);
  if (!sec?.fields) return {};
  const L = (s: string | undefined) => (s ?? "").toLowerCase();

  const mix = findA3FuelMixField(formData);

  const quantity = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      !L(f.name).includes("calorific") &&
      (L(f.name).includes("fuelquantity") ||
        L(f.name).includes("fuel_quantity") ||
        (L(f.name).includes("quantity") &&
          L(f.name).includes("fuel") &&
          !L(f.name).includes("unit")))
  );

  const quantityUnit = sec.fields.find(
    (f) =>
      f.type === "UNIT" &&
      !L(f.name).includes("calorific") &&
      (L(f.name).includes("fuelquantity") ||
        L(f.name).includes("fuel_quantity") ||
        (L(f.name).includes("quantity") && L(f.name).includes("fuel")))
  );

  /** Prefer explicit CMS keys like `A3ProcessFuelConsumptionDensity` (has `fuelconsumptiondensity`, not `fueldensity`). */
  const consumptionDensityField = sec.fields.find((f) => {
    const n = L(f.name);
    if (f.type === "UNIT" || n.includes("unit")) return false;
    return (
      n.includes("fuelconsumptiondensity") ||
      n.includes("processfuelconsumptiondensity")
    );
  });

  const density =
    consumptionDensityField ||
    sec.fields.find(
      (f) =>
        f.type !== "UNIT" &&
        (L(f.name).includes("fueldensity") ||
          L(f.label).includes("fuel density")) &&
        !L(f.name).includes("unit")
    );

  const densityUnit = sec.fields.find(
    (f) =>
      f.type === "UNIT" &&
      (L(f.name).includes("fueldensity") ||
        L(f.name).includes("fuelconsumptiondensity")) &&
      L(f.name).includes("unit")
  );

  const calorific = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      (L(f.name).includes("calorific") ||
        L(f.label).includes("calorific")) &&
      !L(f.name).includes("unit") &&
      !L(f.name).includes("fueldensity")
  );

  const calorificUnit = sec.fields.find(
    (f) =>
      f.type === "UNIT" &&
      L(f.name).includes("calorific") &&
      L(f.name).includes("unit") &&
      !L(f.name).includes("fueldensity")
  );

  return {
    mix: mix?.name,
    quantity: quantity?.name,
    quantityUnit: quantityUnit?.name,
    density: density?.name,
    densityUnit: densityUnit?.name,
    calorific: calorific?.name,
    calorificUnit: calorificUnit?.name,
  };
}

/** True for the fuel density unit field; rendered with kg/m³ only (same as A1/A2). */
export function isA3FuelDensityUnitField(f: FormField): boolean {
  if (f.type !== "UNIT") return false;
  const L = (s: string | undefined) => (s ?? "").toLowerCase();
  const n = L(f.name);
  return (
    (n.includes("fueldensity") || n.includes("fuelconsumptiondensity")) &&
    n.includes("unit")
  );
}

/** True for the fuel density value field (not unit); auto-filled from Fuel Mix, editable like A1/A2. */
export function isA3FuelDensityValueField(f: FormField): boolean {
  const L = (s: string | undefined) => (s ?? "").toLowerCase();
  if (f.type === "UNIT") return false;
  const n = L(f.name);
  if (n.includes("unit")) return false;
  if (
    n.includes("fueldensity") ||
    n.includes("fuelconsumptiondensity") ||
    n.includes("processfuelconsumptiondensity")
  ) {
    return true;
  }
  if (
    L(f.label).includes("fuel density") &&
    !L(f.label).includes("unit")
  ) {
    return true;
  }
  return false;
}

/**
 * Form may store fuel density row id (preferred) or a raw numeric density; API expects a number.
 */
export function resolveA3FuelDensityNumericForSave(
  raw: string,
  rows: FuelDensityApiRow[]
): number {
  const t = String(raw ?? "").trim();
  if (!t) return 0;
  const id = Number(t);
  if (Number.isFinite(id) && rows.some((r) => r.id === id)) {
    const row = rows.find((r) => r.id === id)!;
    const d = Number(row.densityKgL ?? row.densityKgM3 ?? 0);
    return Number.isFinite(d) ? d : 0;
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

/** Type definition — A3Fuel Mix Autofill Ctx. */
export type A3FuelMixAutofillCtx = {
  fuelDensities: FuelDensityApiRow[];
  calorificValues: CalorificValuePublic[];
  densityUnitOptions: UnitSelectOption[];
  calorificUnitOptions: UnitSelectOption[];
  unitIdByToken: Record<string, number>;
};

/** Keys for one fuel row: `fieldName_A3_PROCESS_FUEL_DETAILS_rowN`. */
function rowKey(fieldName: string | undefined, rowIndex: number): string | null {
  if (!fieldName) return null;
  return `${fieldName}_${FUEL_CODE}_row${rowIndex}`;
}

/**
 * Form A1/A2/A3 store raw density numbers (kg/m³ or kg/L) + unit — not catalog row ids.
 * @deprecated Use fuelDensityValueAndUnitFromCatalogRow from fuelMixReferenceAutofill.
 */
export function a1StyleFuelDensityValueAndUnitFromCatalogRow(
  row: FuelDensityApiRow,
  densityUnitOptions: UnitSelectOption[]
): { densityStr: string; unitVal: string } {
  return fuelDensityValueAndUnitFromCatalogRow(row, densityUnitOptions);
}

/**
 * Parse `fieldName_A3_PROCESS_FUEL_DETAILS_rowN` (same idea as A1 `parseFuelMixFieldName`).
 */
export function parseA3ProcessFuelDetailsRowKey(fullKey: string): {
  baseFieldName: string;
  rowIndex: number;
} | null {
  const m = fullKey.match(/^(.+)_A3_PROCESS_FUEL_DETAILS_row(\d+)$/);
  if (!m) return null;
  const rowIndex = Number(m[2]);
  if (!Number.isFinite(rowIndex)) return null;
  return { baseFieldName: m[1], rowIndex };
}

/**
 * True when `fullKey` targets the Fuel Mix control (not density/calorific), for live autofill like Form A1.
 */
export function isA3FuelMixFieldKey(
  formData: FormDataResponse | null,
  fullKey: string
): boolean {
  if (!formData) return false;
  const parsed = parseA3ProcessFuelDetailsRowKey(fullKey);
  if (!parsed) return false;
  const mixField = findA3FuelMixField(formData);
  return mixField?.name === parsed.baseFieldName;
}

/**
 * Fuel density UI: form stores A1-style numeric density + unit; dropdown uses catalog row id for display.
 * `shouldSync` patches when stored values differ from mix-derived fixed density (or blank if no mix match).
 */
export function computeA3FuelDensitySelectState(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  rowIndex: number,
  catalog: FuelDensityApiRow[],
  names: A3FuelFieldNameMap,
  densityUnitOptions: UnitSelectOption[]
): { selectValue: string; shouldSync: boolean } {
  if (!catalog.length || !names.density) {
    return { selectValue: "", shouldSync: false };
  }
  const densityKey = `${names.density}_${FUEL_CODE}_row${rowIndex}`;
  const unitKey = names.densityUnit
    ? `${names.densityUnit}_${FUEL_CODE}_row${rowIndex}`
    : null;
  const raw = String(values[densityKey] ?? "").trim();
  const rawUnit = unitKey ? String(values[unitKey] ?? "").trim() : "";
  const mixName = names.mix;
  const mixVal = mixName
    ? String(values[`${mixName}_${FUEL_CODE}_row${rowIndex}`] ?? "").trim()
    : "";
  let mixLabel = mixVal;
  if (formData && mixName && mixVal) {
    mixLabel = getFuelMixDropdownLabelForField(
      formData,
      FUEL_CODE,
      mixName,
      mixVal
    );
  }
  const derived = mixVal
    ? findFuelDensityForMix(mixVal, mixLabel, catalog)
    : null;

  if (mixVal && derived) {
    const { densityStr, unitVal } = fuelDensityValueAndUnitFromCatalogRow(
      derived,
      densityUnitOptions
    );
    const selectValue = String(derived.id);
    const needsSync =
      raw !== densityStr || (!!unitKey && rawUnit !== unitVal);
    return { selectValue, shouldSync: needsSync };
  }

  /** No mix match: keep density blank (same as A1 when findFuelDensityForMix returns null). */
  if (mixVal && !derived) {
    const needsSync = raw !== "" || (!!unitKey && rawUnit !== "");
    return { selectValue: "", shouldSync: needsSync };
  }

  /** Mix empty: resolve dropdown from stored id or numeric density (legacy / API). */
  let selectValue = "";
  if (raw) {
    if (catalog.some((r) => String(r.id) === raw)) selectValue = raw;
    else {
      const n = Number(raw);
      if (Number.isFinite(n)) {
        const byId = catalog.find((r) => r.id === n);
        if (byId) selectValue = String(byId.id);
        else {
          const byNum = catalog.find(
            (r) =>
              String(r.densityKgL ?? "").trim() === raw ||
              String(r.densityKgM3 ?? "").trim() === raw
          );
          if (byNum) selectValue = String(byNum.id);
        }
      }
    }
  }
  return { selectValue, shouldSync: false };
}

/**
 * Apply fuel mix → density / calorific (and units) for one A3 fuel row.
 * Mutates `patch` with keys to merge into form values.
 */
export function patchA3FuelRowFromFuelMix(
  formData: FormDataResponse,
  names: A3FuelFieldNameMap,
  rowIndex: number,
  mixValue: string,
  ctx: A3FuelMixAutofillCtx,
  patch: Record<string, string>
): void {
  const mixStr = String(mixValue ?? "").trim();
  const mixName = names.mix;
  if (!mixName) return;

  const dk = rowKey(names.density, rowIndex);
  const duk = rowKey(names.densityUnit, rowIndex);
  const ck = rowKey(names.calorific, rowIndex);
  const cuk = rowKey(names.calorificUnit, rowIndex);

  if (!mixStr) {
    if (dk) patch[dk] = "";
    if (duk) patch[duk] = "";
    if (ck) patch[ck] = "";
    if (cuk) patch[cuk] = "";
    return;
  }

  const mixLabel = mixName
    ? getFuelMixDropdownLabelForField(
        formData,
        FUEL_CODE,
        mixName,
        mixStr
      )
    : mixStr;

  const rowData = findFuelDensityForMix(
    mixStr,
    mixLabel,
    ctx.fuelDensities
  );
  if (rowData && dk) {
    const { densityStr, unitVal } = fuelDensityValueAndUnitFromCatalogRow(
      rowData,
      ctx.densityUnitOptions
    );
    patch[dk] = densityStr;
    if (duk) patch[duk] = unitVal;
  } else {
    if (dk) patch[dk] = "";
    if (duk) patch[duk] = "";
  }

  const cvRow = findCalorificValueForMix(
    mixStr,
    mixLabel,
    ctx.calorificValues
  );
  const cvText =
    cvRow?.calorificValue != null
      ? String(cvRow.calorificValue).trim()
      : "";
  if (cvRow && cvText && ck) {
    patch[ck] = cvText;
    if (cuk) {
      patch[cuk] = pickCalorificUnitFromOptions(
        cvRow.unit ?? "",
        ctx.calorificUnitOptions,
        ctx.unitIdByToken
      );
    }
  } else {
    if (ck) patch[ck] = "";
    if (cuk) patch[cuk] = "";
  }
}

/** Recompute density / calorific for every fuel row from the current fuel mix values. */
export function applyA3FuelMixToAllRowsInRecord(
  target: Record<string, string>,
  formData: FormDataResponse,
  rowCount: number,
  ctx: A3FuelMixAutofillCtx,
  names: A3FuelFieldNameMap
): void {
  if (!names.mix) return;
  for (let ri = 0; ri < rowCount; ri++) {
    const mixKey = `${names.mix}_A3_PROCESS_FUEL_DETAILS_row${ri}`;
    const mixVal = String(target[mixKey] ?? "").trim();
    const patch: Record<string, string> = {};
    patchA3FuelRowFromFuelMix(formData, names, ri, mixVal, ctx, patch);
    Object.assign(target, patch);
  }
}
